// pricing-calculator/utils/share.ts
// URL share-state encoding/decoding and clipboard helpers (SSR-safe).

import type { ShareableCalculatorState } from '@dopomogai/types';

type ShareAliases = {
  platform?: string;           // default: 'platform'
  features?: string;           // default: 'features' (comma-separated)
  bundle?: string;             // default: 'bundle'
  roi?: {
    sessions?: string;         // default: 'sessions'
    cvrPct?: string;           // default: 'cvr'
    aov?: string;              // default: 'aov'
    marginPct?: string;        // default: 'margin'
    ticketsMonthly?: string;   // default: 'tickets'
    costPerTicket?: string;    // default: 'cpt'
  };
};

// Add a fully-resolved alias type to avoid string | undefined
type ResolvedAliases = {
  platform: string;
  features: string;
  bundle: string;
  roi: {
    sessions: string;
    cvrPct: string;
    aov: string;
    marginPct: string;
    ticketsMonthly: string;
    costPerTicket: string;
  };
};

const DEFAULT_ALIASES: ResolvedAliases = {
  platform: 'platform',
  features: 'features',
  bundle: 'bundle',
  roi: {
    sessions: 'sessions',
    cvrPct: 'cvr',
    aov: 'aov',
    marginPct: 'margin',
    ticketsMonthly: 'tickets',
    costPerTicket: 'cpt',
  },
};

function resolveAliases(aliases: ShareAliases = {}): ResolvedAliases {
  return {
    platform: aliases.platform ?? DEFAULT_ALIASES.platform,
    features: aliases.features ?? DEFAULT_ALIASES.features,
    bundle: aliases.bundle ?? DEFAULT_ALIASES.bundle,
    roi: {
      sessions: coalesce(aliases.roi?.sessions, DEFAULT_ALIASES.roi.sessions),
      cvrPct: coalesce(aliases.roi?.cvrPct, DEFAULT_ALIASES.roi.cvrPct),
      aov: coalesce(aliases.roi?.aov, DEFAULT_ALIASES.roi.aov),
      marginPct: coalesce(aliases.roi?.marginPct, DEFAULT_ALIASES.roi.marginPct),
      ticketsMonthly: coalesce(aliases.roi?.ticketsMonthly, DEFAULT_ALIASES.roi.ticketsMonthly),
      costPerTicket: coalesce(aliases.roi?.costPerTicket, DEFAULT_ALIASES.roi.costPerTicket),
    },
  };
}

function coalesce(a: string | undefined, b: string): string {
  return a ?? b;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function toURL(input?: string | URL): URL {
  if (input instanceof URL) return input;
  if (typeof input === 'string') return new URL(input, 'http://localhost');
  if (isBrowser()) return new URL(window.location.href);
  // Fallback URL in SSR (will only be used to generate a relative query string)
  return new URL('http://localhost');
}

/**
 * Builds a URLSearchParams from shareable state using short aliases.
 */
export function buildShareParams(
  state: ShareableCalculatorState,
  aliases: ShareAliases = {}
): URLSearchParams {
  const a = resolveAliases(aliases);
  const sp = new URLSearchParams();

  if (state.platformId) sp.set(a.platform, state.platformId);
  if (state.selectedModuleIds && state.selectedModuleIds.length > 0) {
    // Sort for stability
    const csv = [...state.selectedModuleIds].sort().join(',');
    sp.set(a.features, csv);
  }
  if (state.presetId) sp.set(a.bundle, state.presetId);

  if (state.roi) {
    if (state.roi.sessions != null) sp.set(a.roi.sessions, String(state.roi.sessions));
    if (state.roi.cvrPct != null) sp.set(a.roi.cvrPct, String(state.roi.cvrPct));
    if (state.roi.aov != null) sp.set(a.roi.aov, String(state.roi.aov));
    if (state.roi.marginPct != null) sp.set(a.roi.marginPct, String(state.roi.marginPct));
    if (state.roi.ticketsMonthly != null) sp.set(a.roi.ticketsMonthly, String(state.roi.ticketsMonthly));
    if (state.roi.costPerTicket != null) sp.set(a.roi.costPerTicket, String(state.roi.costPerTicket));
  }

  return sp;
}

/**
 * Encode state into a URL string. If baseUrl is omitted in the browser, uses window.location.href.
 * If replaceExisting is true, replaces same-named params; otherwise merges.
 */
export function buildShareUrl(
  state: ShareableCalculatorState,
  options?: {
    baseUrl?: string | URL;
    aliases?: ShareAliases;
    replaceExisting?: boolean;
  }
): string {
  const base = toURL(options?.baseUrl);
  const params = buildShareParams(state, options?.aliases);
  const replace = options?.replaceExisting !== false;

  if (replace) {
    // Replace only our aliased params; keep others intact
    const a = resolveAliases(options?.aliases);
    base.searchParams.delete(a.platform);
    base.searchParams.delete(a.features);
    base.searchParams.delete(a.bundle);
    base.searchParams.delete(a.roi.sessions);
    base.searchParams.delete(a.roi.cvrPct);
    base.searchParams.delete(a.roi.aov);
    base.searchParams.delete(a.roi.marginPct);
    base.searchParams.delete(a.roi.ticketsMonthly);
    base.searchParams.delete(a.roi.costPerTicket);
  }

  // Append our state params
  params.forEach((v, k) => base.searchParams.set(k, v));
  return base.toString();
}

/**
 * Parse shareable state from a URL (or current location) using aliases.
 */
export function readShareStateFromUrl(
  input?: string | URL,
  aliases: ShareAliases = {}
): ShareableCalculatorState {
  const a = resolveAliases(aliases);
  const url = toURL(input);
  const sp = url.searchParams;

  const platformId = sp.get(a.platform) || undefined;
  const featuresCsv = sp.get(a.features) || '';
  const selectedModuleIds = featuresCsv
    ? featuresCsv.split(',').map((s) => s.trim()).filter(Boolean)
    : undefined;
  const presetId = sp.get(a.bundle) || undefined;

  const roi: ShareableCalculatorState['roi'] = {};
  const sessions = sp.get(a.roi.sessions);
  const cvr = sp.get(a.roi.cvrPct);
  const aov = sp.get(a.roi.aov);
  const margin = sp.get(a.roi.marginPct);
  const tickets = sp.get(a.roi.ticketsMonthly);
  const cpt = sp.get(a.roi.costPerTicket);

  if (sessions !== null) roi.sessions = Number(sessions);
  if (cvr !== null) roi.cvrPct = Number(cvr);
  if (aov !== null) roi.aov = Number(aov);
  if (margin !== null) roi.marginPct = Number(margin);
  if (tickets !== null) roi.ticketsMonthly = Number(tickets);
  if (cpt !== null) roi.costPerTicket = Number(cpt);

  // Remove NaNs
  Object.keys(roi).forEach((k) => {
    const key = k as keyof typeof roi;
    if (typeof roi[key] === 'number' && !Number.isFinite(roi[key] as number)) {
      delete roi[key];
    }
  });

  return {
    platformId,
    selectedModuleIds,
    presetId,
    roi: Object.keys(roi).length ? roi : undefined,
  };
}

/**
 * Apply shareable state to current URL (replaceState) safely in the browser.
 */
export function updateBrowserUrlWithState(state: ShareableCalculatorState, aliases?: ShareAliases): void {
  if (!isBrowser()) return;
  const newUrl = buildShareUrl(state, { aliases });
  window.history.replaceState(null, '', newUrl);
}

/**
 * Copy arbitrary text to clipboard. Uses async Clipboard API if available; falls back to a hidden textarea.
 */
export async function copyToClipboard(text: string): Promise<void> {
  if (!isBrowser()) return;
  // Modern API
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(ta);
  }
}

/**
 * Build and copy a share URL from state in one call.
 */
export async function copyShareUrl(
  state: ShareableCalculatorState,
  options?: { baseUrl?: string | URL; aliases?: ShareAliases; replaceExisting?: boolean }
): Promise<string> {
  const url = buildShareUrl(state, options);
  await copyToClipboard(url);
  return url;
}
